/**
 * Compute SLLWP for each leg-2 post from a Cybertote DD Probables matrix.
 *
 *   matrix[leg1_post][leg2_post] = raw string ("--", "0", "12.40", ...)
 *   topLeg1        = [{S, DDPO}, ...] — top-N leg-1 horses in rank order
 *   leg2Posts      = leg-2 post numbers (matrix columns)
 *   weighting      = "equal" | "rank" | "prob"
 *
 * Returns: { [leg2_post]: {sllwp, samples, missing, contributions} }
 *
 * Rules:
 *   - Skip "--" cells (scratched pair / no such combination).
 *   - Skip "0" cells (empty betting pool — noisy in early wagering).
 *   - Weighting:
 *       "equal": SLLWP = mean(samples)
 *       "rank" : 3× rank-1, 2× rank-2, 1× rank-3 (renormalised)
 *       "prob" : weights ∝ 1/DDPO — favours the leg-1 horse the market
 *                thinks is most likely to win. Falls back to "equal" if
 *                any topLeg1 lacks a DDPO value.
 *   - contributions: [{post, rank, value, weight}] for hover tooltips.
 */
export const computeLeg2SLLWP = ({
  matrix,
  topLeg1,
  leg2Posts,
  weighting = "equal",
}) => {
  const out = {};
  const rankWeights = [3, 2, 1];

  // Precompute prob weights: 1/DDPO normalised across topLeg1.
  const invDDPO = topLeg1.map((h) =>
    Number.isFinite(h.DDPO) && h.DDPO > 0 ? 1 / h.DDPO : null
  );
  const canProb = invDDPO.every((v) => v !== null);
  const probSum = canProb ? invDDPO.reduce((a, b) => a + b, 0) : 0;
  const probWeights = canProb ? invDDPO.map((v) => v / probSum) : null;

  const effectiveMode =
    weighting === "prob" && !canProb ? "equal" : weighting;

  for (const y of leg2Posts) {
    const contributions = [];
    for (let i = 0; i < topLeg1.length; i += 1) {
      const x = topLeg1[i].S;
      const row = matrix?.[x] ?? matrix?.[String(x)];
      if (!row) continue;
      const raw = row[y] ?? row[String(y)];
      if (raw === undefined || raw === null) continue;
      const s = String(raw).trim();
      if (s === "" || s === "--") continue;
      const n = Number(s.replace(/[,$]/g, ""));
      if (!Number.isFinite(n) || n <= 0) continue;
      const w =
        effectiveMode === "rank"
          ? rankWeights[i] ?? 0.5
          : effectiveMode === "prob"
          ? probWeights[i]
          : 1;
      contributions.push({ post: x, rank: i + 1, value: n, weight: w });
    }
    if (contributions.length === 0) {
      out[y] = {
        sllwp: null,
        samples: 0,
        missing: topLeg1.length,
        contributions: [],
      };
      continue;
    }
    let sum = 0;
    let wsum = 0;
    for (const c of contributions) {
      sum += c.value * c.weight;
      wsum += c.weight;
    }
    const sllwp = wsum > 0 ? sum / wsum : null;
    out[y] = {
      sllwp: sllwp === null ? null : Number(sllwp.toFixed(2)),
      samples: contributions.length,
      missing: topLeg1.length - contributions.length,
      contributions,
      effectiveMode,
    };
  }
  return out;
};
